import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  Briefcase,
  Building,
  Calendar,
  Car,
  ChevronDown,
  Download,
  FileSearch,
  FileText,
  Leaf,
  Landmark,
  MapPin,
  Search,
  ShieldCheck,
  Users,
  Wrench,
  TrendingUp,
  BarChart3,
  Eye,
  EyeOff,
  Grid,
  List,
  Copy,
  Check,
  ExternalLink,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { openPlanningDoc } from "../../utils/openPlanningDoc";
import { buildFormDataSummary, normalizeFormPayload } from "../../lib/planning/formData";
import { buildApplicationSignals, buildContactCard } from "../../lib/planning/insightMatcher";
import { isLondonCouncil } from "../../lib/councils";

// ═══════════════════════════════════════════════════════════
// CONFIDENCE SCALE (10-point gradient from red to green)
// ═══════════════════════════════════════════════════════════
const CONFIDENCE_SCALE = [
  { min: 0, max: 10, color: '#C45C5C' },
  { min: 10, max: 20, color: '#B8635A' },
  { min: 20, max: 30, color: '#AC6A58' },
  { min: 30, max: 40, color: '#A07156' },
  { min: 40, max: 50, color: '#8C7854' },
  { min: 50, max: 60, color: '#787F52' },
  { min: 60, max: 70, color: '#648650' },
  { min: 70, max: 80, color: '#508D4E' },
  { min: 80, max: 90, color: '#3C8F50' },
  { min: 90, max: 100, color: '#2D7A47' },
];

const getConfidenceColor = (confidence) => {
  if (confidence === null || confidence === undefined) return 'var(--c-text-tertiary)';
  const scale = CONFIDENCE_SCALE.find(s => confidence >= s.min && confidence <= s.max);
  return scale ? scale.color : 'var(--c-text-tertiary)';
};

// City Scope retro radio colors - charred/burnt aesthetic
const RETRO_COLORS = {
  green: "#4A6B4A",   // Charred green - natural/environmental
  red: "#8B4A4A",     // Charred red - critical/required
  orange: "#8B5A2B",  // Charred orange - technical/planning
  blue: "#3D5A6B",    // Charred blue - administrative/support
};

// Section definitions
const SECTION_DEFS = [
  { id: "applicant", label: "Applicant", icon: Users, priority: 1, color: RETRO_COLORS.red },
  { id: "agent", label: "Agent", icon: Briefcase, priority: 2, color: RETRO_COLORS.blue },
  { id: "site", label: "Site", icon: MapPin, priority: 1, color: RETRO_COLORS.green },
  { id: "proposal", label: "Proposal", icon: FileText, priority: 1, color: RETRO_COLORS.orange },
  { id: "development", label: "Development", icon: Building, priority: 1, color: RETRO_COLORS.orange },
  { id: "ownership", label: "Ownership", icon: ShieldCheck, priority: 2, color: RETRO_COLORS.red },
  { id: "access", label: "Access & Parking", icon: Car, priority: 3, color: RETRO_COLORS.blue },
  { id: "environment", label: "Environment", icon: Leaf, priority: 3, color: RETRO_COLORS.green },
  { id: "heritage", label: "Heritage", icon: Landmark, priority: 3, color: RETRO_COLORS.green },
  { id: "materials", label: "Materials", icon: Wrench, priority: 3, color: RETRO_COLORS.orange },
  { id: "dates", label: "Dates", icon: Calendar, priority: 2, color: RETRO_COLORS.blue },
  { id: "fees", label: "Fees", icon: FileText, priority: 3, color: RETRO_COLORS.blue },
  { id: "documents", label: "Supporting Docs", icon: FileText, priority: 3, color: RETRO_COLORS.blue },
  { id: "other", label: "Other Details", icon: FileText, priority: 4, color: RETRO_COLORS.blue },
];

const normalizeLabel = (value) => {
  if (!value) return "";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const PHONE_REGEX = /(\+?\d[\d\s().-]{6,}\d)/;
const SENSITIVE_KEY_PATTERNS = [
  /email/i,
  /phone|mobile|telephone/i,
  /address|postcode|post code|uprn|grid/i,
  /applicant|agent|owner|contact/i,
  /name|surname|first name|last name/i,
];
const KEY_FIELD_PATTERNS = [
  /applicant|agent|owner/i,
  /address|postcode|uprn/i,
  /proposal|description/i,
  /development|use class/i,
  /site area|area|size/i,
  /decision|status|received|validated/i,
];

const isSensitiveKey = (key) =>
  SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key || ""));

const isSensitiveValue = (value) => {
  if (!value || typeof value !== "string") return false;
  return EMAIL_REGEX.test(value) || PHONE_REGEX.test(value);
};


const parseNumeric = (value) => {
  if (value === null || value === undefined) return null;
  const match = String(value).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const num = Number(match[0]);
  return Number.isNaN(num) ? null : num;
};

const formatNumber = (value, decimals = 0) => {
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatAreaValue = (value) => {
  const num = parseNumeric(value);
  if (num === null) {
    return { value: String(value), unit: "" };
  }
  const text = String(value).toLowerCase();
  if (/ha|hectare/.test(text)) {
    return { value: formatNumber(num, 2), unit: "ha" };
  }
  if (/m2|m²|sqm|sq m|square metre|square meter/.test(text)) {
    return { value: formatNumber(num), unit: "m²" };
  }
  if (/acre/.test(text)) {
    return { value: formatNumber(num, 2), unit: "acres" };
  }
  if (num >= 10000) {
    return {
      value: formatNumber(num),
      unit: "m²",
      secondary: `${formatNumber(num / 10000, 2)} ha`,
    };
  }
  return { value: formatNumber(num), unit: "m²" };
};

const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightMatches = (text, query) => {
  if (!query || !text) return text;
  const tokens = query
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (!tokens.length) return text;
  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "ig");
  const parts = String(text).split(pattern);
  return parts.map((part, idx) =>
    idx % 2 === 1 ? (
      <mark
        key={`${part}-${idx}`}
        className="rounded-sm px-0.5 bg-[var(--c-accent)]/25 text-[var(--c-text-primary)]"
      >
        {part}
      </mark>
    ) : (
      <span key={`${part}-${idx}`}>{part}</span>
    )
  );
};

const findFormDocument = (application) => {
  const documents = application?.documents || [];
  return documents.find((doc) => {
    const haystack = [
      doc?.title,
      doc?.name,
      doc?.description,
      doc?.doc_type,
      doc?.document_type,
      doc?.category,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return /application form|application|form|proposal/i.test(haystack);
  });
};

export const resolveApplicationFormDocumentLinks = (application, formData) => {
  const formDoc = findFormDocument(application);
  const formDocLink =
    formDoc?.doc_link ||
    formDoc?.document_url ||
    formDoc?.document_link ||
    formDoc?.pdf_url ||
    formDoc?.url ||
    formDoc?.link ||
    formData?.pdf_url ||
    formData?.form_pdf_url ||
    formData?.document_url ||
    null;
  const appLink =
    application?.link ||
    application?.app_link ||
    application?.application_link ||
    application?.application_url ||
    formData?.source_link ||
    formData?.source_url ||
    null;

  return {
    formDoc: formDoc || (formDocLink ? {
      pdf_url: formDocLink,
      source_desc: "Application form",
    } : null),
    formDocLink,
    appLink,
  };
};

const toSearchText = (value) => (value || "").toString().toLowerCase();

const formatValue = (value, depth = 0) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    return value
      .map((item) => formatValue(item, depth + 1))
      .filter(Boolean)
      .join("; ");
  }
  if (typeof value === "object") {
    if (depth > 2) {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    const entries = Object.entries(value);
    if (!entries.length) return "";
    return entries
      .map(([key, val]) => {
        const formatted = formatValue(val, depth + 1);
        if (!formatted) return "";
        return `${normalizeLabel(key)}: ${formatted}`;
      })
      .filter(Boolean)
      .join("; ");
  }
  return String(value);
};

const normalizeConfidence = (value) => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return Math.min(100, Math.max(0, Math.round(percent)));
};

const inferGroup = (text) => {
  const value = toSearchText(text);
  if (!value) return "other";
  if (/applicant/.test(value)) return "applicant";
  if (/agent|consultant/.test(value)) return "agent";
  if (/site|location|address|postcode|ward|parish|grid/.test(value)) return "site";
  if (/proposal|description of development|development description|works/.test(value)) return "proposal";
  if (/development|project|scheme/.test(value)) return "development";
  if (/ownership|owner|certificate|interest/.test(value)) return "ownership";
  if (/access|parking|transport|traffic|road/.test(value)) return "access";
  if (/environment|drainage|flood|tree|ecology|biodiversity|contamination/.test(value)) return "environment";
  if (/heritage|listed|conservation|archaeology/.test(value)) return "heritage";
  if (/material|appearance|design/.test(value)) return "materials";
  if (/date|received|validated|decision/.test(value)) return "dates";
  if (/fee|payment|charge/.test(value)) return "fees";
  if (/document|drawing|plan|statement|report/.test(value)) return "documents";
  if (/contact|email|phone|telephone/.test(value)) return "agent";
  return "other";
};

const _parseFieldItem = (item, fallbackGroup) => {
  if (!item) return [];
  if (Array.isArray(item)) {
    return item.flatMap((entry) => _parseFieldItem(entry, fallbackGroup));
  }
  if (typeof item !== "object") {
    const value = formatValue(item);
    if (!value) return [];
    return [
      {
        key: `value-${value.slice(0, 24)}`,
        label: "Value",
        value,
        group: fallbackGroup || "other",
      },
    ];
  }

  const label =
    item.label ||
    item.field ||
    item.name ||
    item.question ||
    item.key ||
    item.id;
  const rawValue =
    item.value ||
    item.answer ||
    item.answer_normalized ||
    item.answerNormalized ||
    item.answer_raw ||
    item.answerRaw ||
    item.response ||
    item.text ||
    item.content ||
    item.data ||
    item.result;
  const isCheckbox =
    item.is_checkbox_option !== undefined
      ? item.is_checkbox_option
      : item.isCheckboxOption;
  const isChecked =
    item.is_checked !== undefined ? item.is_checked : item.isChecked;
  if (isCheckbox && isChecked === false) {
    return [];
  }
  const value = formatValue(rawValue);
  let group =
    item.group ||
    item.section ||
    item.category ||
    item.group_id ||
    item.groupId ||
    fallbackGroup ||
    inferGroup(label || item.key);
  const labelText = toSearchText(label);
  const valueText = toSearchText(value);
  if (labelText && /applicant.*agent|agent.*applicant/.test(labelText)) {
    if (valueText.includes("agent")) group = "agent";
    if (valueText.includes("applicant")) group = "applicant";
  }
  const confidence = normalizeConfidence(item.confidence || item.score || item.confidence_score);

  if (!label && typeof rawValue === "object") {
    return flattenObject(rawValue, fallbackGroup);
  }

  const keyBase = item.key || item.id || label;
  const keySuffix = [
    item.group_id || item.groupId,
    item.page != null ? `p${item.page}` : null,
    value ? value.slice(0, 24) : null,
  ]
    .filter(Boolean)
    .join("-");
  const key = keyBase ? `${keyBase}-${keySuffix}` : `field-${Math.random().toString(36).slice(2, 8)}`;

  return [
    {
      key,
      label: normalizeLabel(label || "Field"),
      value,
      group,
      confidence,
      raw: item,
    },
  ];
};

const flattenObject = (obj, groupHint, prefix = "") => {
  if (!obj || typeof obj !== "object") return [];
  const entries = Object.entries(obj);
  if (!entries.length) return [];

  return entries.flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "object") {
        return value.flatMap((entry, index) =>
          flattenObject(entry, groupHint || key, `${path}[${index}]`)
        );
      }
      const formatted = formatValue(value);
      return [
        {
          key: path,
          label: normalizeLabel(key),
          value: formatted,
          group: groupHint || inferGroup(path),
        },
      ];
    }
    if (value && typeof value === "object") {
      return flattenObject(value, groupHint || key, path);
    }
    const formatted = formatValue(value);
    return [
      {
        key: path,
        label: normalizeLabel(key),
        value: formatted,
        group: groupHint || inferGroup(path),
      },
    ];
  });
};

const normalizePayload = (payload) => normalizeFormPayload(payload);

const buildFields = (normalized) => normalized?.fields || [];

const formatDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// ── Insight extraction helpers ──────────────────────────────

const findField = (fields, pattern, { group, hasDigits, isYes } = {}) => {
  for (const f of fields) {
    if (!f.value) continue;
    if (group && f.group !== group) continue;
    const haystack = [f.label, f.field_key, f.key, f.question_contextual, f.question].filter(Boolean).join(" ");
    if (!pattern.test(haystack)) continue;
    if (hasDigits && !/\d/.test(f.value)) continue;
    if (isYes && !/^yes\b/i.test(String(f.value).trim())) continue;
    return f;
  }
  return null;
};

const findCheckedField = (fields, pattern) => {
  for (const f of fields) {
    if (!f.is_checkbox_option || !f.is_checked) continue;
    const haystack = [f.label, f.field_key, f.key, f.field_key_base, f.question_contextual].filter(Boolean).join(" ");
    if (pattern.test(haystack)) return f;
  }
  return null;
};

const findFieldByKey = (fields, predicate) => {
  for (const f of fields) {
    if (!f?.value) continue;
    const key = String(f.field_key_base || f.field_key || f.key || "").toLowerCase();
    if (!key) continue;
    if (predicate(key, f)) return f;
  }
  return null;
};

const countFieldsInGroup = (fields, group) =>
  fields.filter(f => f.group === group && f.value).length;

const boolLabel = (field) => {
  const v = String(field?.value ?? "").trim().toLowerCase();
  if (/^yes\b/.test(v)) return "Yes";
  if (/^no\b/.test(v)) return "No";
  return field?.value || null;
};

// ── Multi-field finder (returns all matches, not just first) ──
const findAllFields = (fields, pattern, opts = {}) => {
  const results = [];
  for (const f of fields) {
    if (!f.value) continue;
    if (opts.group && f.group !== opts.group) continue;
    const haystack = [f.label, f.field_key, f.key, f.question_contextual, f.question, f.context_label, f.context_id, f.group_id, f.searchText].filter(Boolean).join(" ");
    if (!pattern.test(haystack)) continue;
    if (opts.hasDigits && !/\d/.test(f.value)) continue;
    if (opts.isYes && !/^yes\b/i.test(String(f.value).trim())) continue;
    results.push(f);
  }
  return results;
};

// ── Short-value insight pusher (avoids bloating with long text) ──
const pushShortValue = (insights, field, label, icon, type, sectionId, maxLen = 80) => {
  if (!field) return;
  const val = String(field.value).trim();
  if (val.length < 2 || val.length > maxLen) return;
  if (/^(n\/a|na|none|not provided|null|nil|-+)$/i.test(val)) return;
  insights.push({ icon, label, value: val, type, sectionId: sectionId || field.group || "other" });
};

// Extract insights from fields
const extractInsights = (fields) => {
  const insights = [];
  const seen = new Set(); // dedupe by label

  const push = (obj) => {
    if (seen.has(obj.label)) return;
    seen.add(obj.label);
    insights.push(obj);
  };

  // ── Application type ─────────────────────────────────
  const appTypeField = findField(fields, /application.*type|type.*of.*application|type.*of.*planning/i);
  if (appTypeField) {
    const val = String(appTypeField.value).trim();
    if (val.length >= 2 && val.length <= 80) {
      push({ icon: FileText, label: "Application Type", value: val, type: "info", sectionId: "other" });
    }
  }

  // ── Area / size ──────────────────────────────────────
  const areaField = findField(fields, /site\s*area|area\s*of\s*site|area.*hectare|area.*sq|size.*site|total\s*site/i, { hasDigits: true });
  if (areaField) {
    const formatted = formatAreaValue(areaField.value);
    push({ icon: TrendingUp, label: "Site Area", value: formatted.value, unit: formatted.unit, secondary: formatted.secondary, type: "metric", sectionId: areaField.group || "site" });
  }

  // ── Floor space (existing) ───────────────────────────
  const existFloorField = findField(fields, /existing.*floor\s*space|gross.*floor.*existing|existing.*gross|floor\s*area.*existing/i, { hasDigits: true });
  if (existFloorField) {
    const formatted = formatAreaValue(existFloorField.value);
    push({ icon: Building, label: "Existing Floor Space", value: formatted.value, unit: formatted.unit || "m²", type: "metric", sectionId: existFloorField.group || "development" });
  }

  // ── Floor space (proposed) ───────────────────────────
  const propFloorField = findField(fields, /proposed.*floor\s*space|gross.*floor.*proposed|proposed.*gross|floor\s*area.*proposed|total.*floor\s*space/i, { hasDigits: true });
  if (propFloorField && propFloorField !== existFloorField) {
    const formatted = formatAreaValue(propFloorField.value);
    push({ icon: Building, label: "Proposed Floor Space", value: formatted.value, unit: formatted.unit || "m²", type: "metric", sectionId: propFloorField.group || "development" });
  }

  // ── Parking spaces ───────────────────────────────────
  const parkingField = findField(fields, /parking\s*space|vehicle\s*space|car\s*space|no.*of.*parking|number.*parking/i, { hasDigits: true });
  if (parkingField) {
    const parkingCount = parseNumeric(parkingField.value);
    push({ icon: Car, label: "Parking Spaces", value: parkingCount !== null ? formatNumber(parkingCount) : parkingField.value, unit: "spaces", type: "metric", sectionId: parkingField.group || "access" });
  }

  // ── Dwelling / unit count ────────────────────────────
  const dwellingField = findField(fields, /dwelling|residential\s*unit|number.*unit|total.*unit|proposed.*unit|housing.*unit/i, { hasDigits: true });
  if (dwellingField) {
    const count = parseNumeric(dwellingField.value);
    if (count !== null && count > 0) {
      push({ icon: Building, label: "Dwellings", value: formatNumber(count), unit: count === 1 ? "unit" : "units", type: "metric", sectionId: dwellingField.group || "development" });
    }
  }

  // ── Bedrooms ─────────────────────────────────────────
  const bedroomField = findField(fields, /bedroom|no.*of.*bed|number.*bed/i, { hasDigits: true });
  if (bedroomField) {
    const count = parseNumeric(bedroomField.value);
    if (count !== null && count > 0) {
      push({ icon: Building, label: "Bedrooms", value: formatNumber(count), unit: count === 1 ? "bedroom" : "bedrooms", type: "metric", sectionId: bedroomField.group || "development" });
    }
  }

  // ── Height ───────────────────────────────────────────
  const heightField = findField(fields, /height.*proposed|proposed.*height|max.*height|building.*height|ridge.*height/i, { hasDigits: true });
  if (heightField) {
    const num = parseNumeric(heightField.value);
    if (num !== null && num > 0) {
      push({ icon: Building, label: "Building Height", value: `${formatNumber(num, num < 100 ? 1 : 0)}`, unit: "m", type: "metric", sectionId: heightField.group || "development" });
    }
  }

  // ── Existing use ─────────────────────────────────────
  const existingUseField = findField(fields, /existing.*use|current.*use|present.*use|use.*class.*exist/i);
  if (existingUseField) {
    const val = String(existingUseField.value).trim();
    if (val.length > 1 && val.length <= 80) {
      push({ icon: FileText, label: "Existing Use", value: val, type: "info", sectionId: existingUseField.group || "development" });
    }
  }

  // ── Proposed use ─────────────────────────────────────
  const proposedUseField = findField(fields, /proposed.*use|new.*use|use.*class.*propos/i);
  if (proposedUseField && proposedUseField.value !== existingUseField?.value) {
    const val = String(proposedUseField.value).trim();
    if (val.length > 1 && val.length <= 80) {
      push({ icon: FileText, label: "Proposed Use", value: val, type: "info", sectionId: proposedUseField.group || "development" });
    }
  }

  // ── Employees (existing) ─────────────────────────────
  const existEmpField = findField(fields, /existing.*employ|current.*employ|no.*employ.*exist|employee.*exist/i, { hasDigits: true });
  if (existEmpField) {
    const count = parseNumeric(existEmpField.value);
    if (count !== null && count >= 0) {
      push({ icon: Users, label: "Existing Employees", value: formatNumber(count), type: "metric", sectionId: existEmpField.group || "development" });
    }
  }

  // ── Employees (proposed) ─────────────────────────────
  const propEmpField = findField(fields, /proposed.*employ|new.*employ|no.*employ.*propos|employee.*propos/i, { hasDigits: true });
  if (propEmpField && propEmpField !== existEmpField) {
    const count = parseNumeric(propEmpField.value);
    if (count !== null && count >= 0) {
      push({ icon: Users, label: "Proposed Employees", value: formatNumber(count), type: "metric", sectionId: propEmpField.group || "development" });
    }
  }

  // ── Hours of operation ───────────────────────────────
  const hoursField = findField(fields, /hours.*operation|opening.*hours|operating.*hours|business.*hours/i);
  if (hoursField) {
    pushShortValue(insights, hoursField, "Hours of Operation", Calendar, "info", "development");
    if (insights.length > 0 && insights[insights.length - 1].label === "Hours of Operation") seen.add("Hours of Operation");
  }

  // ── Trees ────────────────────────────────────────────
  const treesField = findField(fields, /tree.*on|tree.*adjacent|tree.*site|any.*tree|tree.*affect|tree.*fell|tree.*remov/i);
  if (treesField) {
    const label = boolLabel(treesField);
    if (label) push({ icon: Leaf, label: "Trees on Site", value: label, type: "flag", sectionId: "environment" });
  }

  // ── Protected species ────────────────────────────────
  const speciesField = findField(fields, /protected.*species|wildlife|great.*crested.*newt|bat.*survey|badger|ecology|biodiversity/i);
  if (speciesField) {
    const label = boolLabel(speciesField);
    if (label) push({ icon: Leaf, label: "Protected Species", value: label, type: "flag", sectionId: "environment" });
  }

  // ── Listed building ──────────────────────────────────
  const listedField = findField(fields, /listed.*building|grade.*listed|statutory.*list|nationally.*listed/i);
  if (listedField) {
    const label = boolLabel(listedField);
    if (label) push({ icon: Landmark, label: "Listed Building", value: label, type: "flag", sectionId: "heritage" });
  }

  // ── Conservation area ────────────────────────────────
  const conservationField = findField(fields, /conservation.*area/i);
  if (conservationField) {
    const label = boolLabel(conservationField);
    if (label) push({ icon: Landmark, label: "Conservation Area", value: label, type: "flag", sectionId: "heritage" });
  }

  // ── Scheduled monument ───────────────────────────────
  const monumentField = findField(fields, /scheduled.*monument|ancient.*monument|archaeological/i);
  if (monumentField) {
    const label = boolLabel(monumentField);
    if (label) push({ icon: Landmark, label: "Scheduled Monument", value: label, type: "flag", sectionId: "heritage" });
  }

  // ── World heritage site ──────────────────────────────
  const worldHeritageField = findField(fields, /world.*heritage|UNESCO/i);
  if (worldHeritageField) {
    const label = boolLabel(worldHeritageField);
    if (label) push({ icon: Landmark, label: "World Heritage Site", value: label, type: "flag", sectionId: "heritage" });
  }

  // ── Flood risk ───────────────────────────────────────
  const floodField = findField(fields, /flood.*zone|flood.*risk|liable.*flood|flood.*area|flood.*plain/i);
  if (floodField) {
    const val = String(floodField.value).trim();
    // Try to extract zone number or boolean
    const zoneMatch = val.match(/zone\s*(\d[a-b]?)/i);
    const label = zoneMatch ? `Zone ${zoneMatch[1]}` : boolLabel(floodField);
    if (label) push({ icon: AlertCircle, label: "Flood Risk", value: label, type: "flag", sectionId: floodField.group || "environment" });
  }

  // ── Contamination ────────────────────────────────────
  const hazardField = findField(fields, /hazardous|contaminated|contamination|land.*affected|potentially.*contam/i);
  if (hazardField) {
    const label = boolLabel(hazardField);
    if (label) push({ icon: AlertCircle, label: "Contamination", value: label, type: "flag", sectionId: hazardField.group || "environment" });
  }

  // ── Foul sewage disposal ─────────────────────────────
  const sewageField = findField(fields, /foul.*sewage|sewage.*disposal|foul.*drainage|mains.*sewer/i);
  if (sewageField) {
    const val = String(sewageField.value).trim();
    if (val.length >= 2 && val.length <= 60) {
      push({ icon: AlertCircle, label: "Foul Sewage", value: val, type: "info", sectionId: "environment" });
    }
  }

  // ── Surface water drainage ───────────────────────────
  const surfaceWaterField = findField(fields, /surface.*water|surface.*drainage|suds|sustainable.*drainage/i);
  if (surfaceWaterField) {
    const val = String(surfaceWaterField.value).trim();
    if (val.length >= 2 && val.length <= 60) {
      push({ icon: AlertCircle, label: "Surface Water", value: val, type: "info", sectionId: "environment" });
    }
  }

  // ── Vehicle access ───────────────────────────────────
  const accessField = findField(fields, /vehicle.*access|new.*access|altered.*access|access.*public.*road|access.*highway/i);
  if (accessField) {
    const label = boolLabel(accessField);
    if (label) push({ icon: Car, label: "Access Altered", value: label, type: "flag", sectionId: accessField.group || "access" });
  }

  // ── Public right of way ──────────────────────────────
  const prowField = findField(fields, /public.*right.*of.*way|footpath|bridleway|public.*path/i);
  if (prowField) {
    const label = boolLabel(prowField);
    if (label) push({ icon: MapPin, label: "Public Right of Way", value: label, type: "flag", sectionId: prowField.group || "access" });
  }

  // ── Waste / refuse storage ───────────────────────────
  const wasteField = findField(fields, /waste.*storage|refuse.*storage|bin.*storage|recycling.*facilit/i);
  if (wasteField) {
    const label = boolLabel(wasteField);
    if (label) push({ icon: AlertCircle, label: "Waste Storage", value: label, type: "flag", sectionId: "environment" });
  }

  // ── Ownership certificate ────────────────────────────
  const certField =
    findFieldByKey(fields, (key) => {
      if (key.includes("certificate_signed")) return false;
      return (
        key === "ownership_certificate" ||
        key.includes("ownership_certificate") ||
        key.includes("land_ownership_certificate_is_required")
      );
    }) ||
    findField(fields, /ownership.*certificate|certificate.*[ABCD]|certificate.*served|ownership.*type/i);
  if (certField) {
    const val = String(certField.value).trim();
    const certMatch = val.match(/\b(?:certificate\s*)?([ABCD])\b/i);
    if (certMatch) {
      push({ icon: ShieldCheck, label: "Ownership Certificate", value: `Certificate ${certMatch[1].toUpperCase()}`, type: "info", sectionId: "ownership" });
    } else if (/certificate/i.test(val) && val.length >= 2 && val.length <= 40) {
      push({ icon: ShieldCheck, label: "Ownership Certificate", value: val, type: "info", sectionId: "ownership" });
    }
  }

  // ── Pre-application advice ───────────────────────────
  const preAppField = findField(fields, /pre.*application|pre-app|prior.*advice|early.*advice/i);
  if (preAppField) {
    const label = boolLabel(preAppField);
    if (label) push({ icon: FileText, label: "Pre-application Advice", value: label, type: "flag", sectionId: "other" });
  }

  // ── Pre-app reference ────────────────────────────────
  const preAppRefField = findField(fields, /pre.*application.*ref|pre-app.*ref|advice.*reference/i);
  if (preAppRefField) {
    const val = String(preAppRefField.value).trim();
    if (val.length >= 2 && val.length <= 40) {
      push({ icon: FileText, label: "Pre-app Reference", value: val, type: "info", sectionId: "other" });
    }
  }

  // ── Site visible from public land ────────────────────
  const siteVisibleField = findField(fields, /site.*seen.*public|visible.*public|view.*from.*public|site.*visit/i);
  if (siteVisibleField) {
    const label = boolLabel(siteVisibleField);
    if (label) push({ icon: Eye, label: "Site Visible from Public", value: label, type: "flag", sectionId: "site" });
  }

  // ── Authority employee ───────────────────────────────
  const authEmpField = findField(fields, /authority.*employ|council.*employ|member.*council|related.*officer|member.*of.*staff/i);
  if (authEmpField) {
    const label = boolLabel(authEmpField);
    if (label) push({ icon: Users, label: "Authority Employee", value: label, type: "flag", sectionId: "other" });
  }

  // ── CIL / Exemption ──────────────────────────────────
  const cilField = findField(fields, /community.*infrastructure|CIL|self.*build.*exemption|cil.*liable|cil.*exempt/i);
  if (cilField) {
    const val = String(cilField.value).trim();
    if (val.length >= 2 && val.length <= 60) {
      push({ icon: FileText, label: "CIL", value: val, type: "info", sectionId: "fees" });
    }
  }

  // ── Renewable energy / sustainability ────────────────
  const renewableField = findField(fields, /renewable.*energy|solar.*panel|heat.*pump|low.*carbon|energy.*efficien|sustainable.*construct/i);
  if (renewableField) {
    const label = boolLabel(renewableField);
    if (label) push({ icon: Leaf, label: "Renewable Energy", value: label, type: "flag", sectionId: "environment" });
  }

  // ── Telecommunications equipment ─────────────────────
  const telecomField = findField(fields, /telecommunication|telecom|mast|antenna|aerial/i);
  if (telecomField) {
    const label = boolLabel(telecomField);
    if (label) push({ icon: Building, label: "Telecoms Equipment", value: label, type: "flag", sectionId: "development" });
  }

  // ── Industrial / commercial processes ────────────────
  const industrialField = findField(fields, /industrial.*process|commercial.*process|trade.*effluent|manufacturing/i);
  if (industrialField) {
    const label = boolLabel(industrialField);
    if (label) push({ icon: Building, label: "Industrial Process", value: label, type: "flag", sectionId: "development" });
  }

  // ── Boundary treatment ───────────────────────────────
  const boundaryField = findField(fields, /boundary.*treat|fence|wall.*boundary|means.*of.*enclos/i);
  if (boundaryField) {
    const val = String(boundaryField.value).trim();
    if (val.length >= 2 && val.length <= 60) {
      push({ icon: MapPin, label: "Boundary Treatment", value: val, type: "info", sectionId: "materials" });
    }
  }

  // ── Landscaping ──────────────────────────────────────
  const landscapeField = findField(fields, /landscaping|landscape.*scheme|planting|soft.*landscap|hard.*landscap/i);
  if (landscapeField) {
    const label = boolLabel(landscapeField);
    if (label) push({ icon: Leaf, label: "Landscaping", value: label, type: "flag", sectionId: "environment" });
  }

  // ── Demolition ───────────────────────────────────────
  const demolitionField = findField(fields, /demolition|demolish|building.*to.*be.*removed/i);
  if (demolitionField) {
    const label = boolLabel(demolitionField);
    if (label) push({ icon: AlertCircle, label: "Demolition", value: label, type: "flag", sectionId: "development" });
  }

  // ── Advertisements / signage ─────────────────────────
  const advertField = findField(fields, /advertisement|advert.*consent|signage|illuminat.*sign/i);
  if (advertField) {
    const label = boolLabel(advertField);
    if (label) push({ icon: FileText, label: "Advertisements", value: label, type: "flag", sectionId: "development" });
  }

  // ── Ward / parish ────────────────────────────────────
  const wardField = findField(fields, /\bward\b/i, { group: "site" });
  if (wardField) {
    const val = String(wardField.value).trim();
    if (val.length >= 2 && val.length <= 50) {
      push({ icon: MapPin, label: "Ward", value: val, type: "info", sectionId: "site" });
    }
  }

  const parishField = findField(fields, /\bparish\b/i, { group: "site" });
  if (parishField) {
    const val = String(parishField.value).trim();
    if (val.length >= 2 && val.length <= 50 && val !== wardField?.value) {
      push({ icon: MapPin, label: "Parish", value: val, type: "info", sectionId: "site" });
    }
  }

  // ── Materials mentioned ──────────────────────────────
  const materialsCount = countFieldsInGroup(fields, "materials");
  if (materialsCount > 0) {
    push({ icon: Wrench, label: "Materials", value: formatNumber(materialsCount), unit: materialsCount === 1 ? "item" : "items", type: "info", sectionId: "materials" });
  }

  // ── Environmental items ──────────────────────────────
  const envCount = countFieldsInGroup(fields, "environment");
  if (envCount > 0) {
    push({ icon: Leaf, label: "Environmental Items", value: formatNumber(envCount), unit: envCount === 1 ? "item" : "items", type: "info", sectionId: "environment" });
  }

  // ── Heritage items ───────────────────────────────────
  const heritageCount = countFieldsInGroup(fields, "heritage");
  if (heritageCount > 0) {
    push({ icon: Landmark, label: "Heritage Items", value: formatNumber(heritageCount), unit: heritageCount === 1 ? "item" : "items", type: "info", sectionId: "heritage" });
  }

  // ── Access & parking items ───────────────────────────
  const accessCount = countFieldsInGroup(fields, "access");
  if (accessCount > 0) {
    push({ icon: Car, label: "Access & Parking", value: formatNumber(accessCount), unit: accessCount === 1 ? "field" : "fields", type: "info", sectionId: "access" });
  }

  // ── Supporting documents ─────────────────────────────
  const docsCount = countFieldsInGroup(fields, "documents");
  if (docsCount > 0) {
    push({ icon: FileText, label: "Supporting Docs", value: formatNumber(docsCount), unit: docsCount === 1 ? "item" : "items", type: "info", sectionId: "documents" });
  }

  // ── Ownership fields ─────────────────────────────────
  const ownershipCount = countFieldsInGroup(fields, "ownership");
  if (ownershipCount > 0) {
    push({ icon: ShieldCheck, label: "Ownership Fields", value: formatNumber(ownershipCount), unit: ownershipCount === 1 ? "field" : "fields", type: "info", sectionId: "ownership" });
  }

  // ── Dates mentioned ──────────────────────────────────
  const startDateField = findField(fields, /start.*date|commencement.*date|date.*work.*start|date.*commenc/i);
  if (startDateField) {
    const formatted = formatDate(startDateField.value);
    if (formatted) push({ icon: Calendar, label: "Start Date", value: formatted, type: "info", sectionId: startDateField.group || "dates" });
  }

  const completionDateField = findField(fields, /completion.*date|date.*complet|estimated.*completion|date.*finish/i);
  if (completionDateField) {
    const formatted = formatDate(completionDateField.value);
    if (formatted) push({ icon: Calendar, label: "Completion Date", value: formatted, type: "info", sectionId: completionDateField.group || "dates" });
  }

  // ── Fee amount ───────────────────────────────────────
  const feeField = findField(fields, /fee.*amount|application.*fee|total.*fee|fee.*payable|fee.*£|£.*fee/i, { hasDigits: true });
  if (feeField) {
    const num = parseNumeric(feeField.value);
    if (num !== null && num > 0) {
      push({ icon: FileText, label: "Application Fee", value: `£${formatNumber(num, num % 1 !== 0 ? 2 : 0)}`, type: "metric", sectionId: feeField.group || "fees" });
    }
  }

  // ── Catch-all: scan remaining boolean yes/no fields ──
  // Pick up any yes/no flags we haven't already captured
  for (const f of fields) {
    if (insights.length >= 30) break; // cap total insights
    if (!f.value) continue;
    const val = String(f.value).trim().toLowerCase();
    if (!/^(yes|no)$/i.test(val)) continue;
    const label = f.label || f.question_contextual || "";
    if (!label || label.length < 4 || label.length > 50) continue;
    if (seen.has(label)) continue;
    // Skip people/contact/address fields
    if (/email|phone|name|surname|first|last|address|postcode|title|company|organisation/i.test(label)) continue;
    // Only include fields that sound like planning signals
    if (!/tree|flood|heritage|listed|conserv|access|parking|hazard|contam|species|ecology|right.*of.*way|waste|demolit|renewable|solar|drain|sewer|boundary|landscap|monument|archaeol|telecom|industrial|advert|sign|visible|authority|council|pre.*app|CIL|exempt|protect|environ|biodivers|neighbour|highway|traffic|noise|air.*quality|lighting|green.*belt|AONB|SSSI|SPA|SAC|ramsar/i.test(label)) continue;
    push({ icon: AlertCircle, label, value: val === "yes" ? "Yes" : "No", type: "flag", sectionId: f.group || "other" });
  }

  return insights;
};

// ── Label abbreviation ──────────────────────────────────────

// field_key_base (or field_key) → short label  (highest priority)
// Matched against field_key_base first, then field_key (lowercased)
const FIELD_KEY_BASE_MAP = {
  // ─── Top-level ───────────────────────────────────────
  "online_reference": "Online Ref",
  "application_reference": "App Ref",
  "submitted_by": "Submitted By",
  "acting_as": "Acting As",
  // ─── Applicant/Agent role ────────────────────────────
  "applicant_or_agent_details.are_you_an_applicant_or_an_agent_an_agent_is_an_architect_consultant_or_someone_else_acting_on_behalf_of_the_applicant_in_connection_with_this_application": "Role",
  "application_role.applicant_or_agent": "Role",
  // ─── Description / Proposal ──────────────────────────
  "description_of_proposal.please_describe_accurately_the_work_proposed_max_500_characters": "Proposal",
  "description_of_proposal.has_the_work_already_been_started_and_or_completed": "Work Started",
  "description.proposal": "Proposal",
  "description.description_of_proposal": "Proposal",
  "description.work_proposed": "Work Proposed",
  "description.variation_sought": "Variation",
  "description.variation_summary": "Variation",
  "description.description_of_development": "Description",
  "description.development_description": "Description",
  "development_status.commencement": "Commenced",
  "development_status.work_started": "Work Started",
  "development_status.work_already_started_or_completed": "Work Started",
  // ─── Agent Details ───────────────────────────────────
  "agent_details.title": "Title",
  "agent_details.other_title": "Other Title",
  "agent_details.first_name": "First Name",
  "agent_details.last_name": "Surname",
  "agent_details.surname": "Surname",
  "agent_details.name": "Name",
  "agent_details.company_organisation": "Company",
  "agent_details.company": "Company",
  "agent_details.organisation": "Organisation",
  "agent_details.ref_number": "Ref No.",
  "agent_details.email_address": "Email",
  "agent_details.email": "Email",
  "agent_details.telephone_number": "Phone",
  "agent_details.telephone": "Phone",
  "agent_details.extension_number": "Extension",
  "agent_details.mobile_number": "Mobile",
  "agent_details.mobile": "Mobile",
  "agent_details.fax_number": "Fax",
  "agent_details.fax": "Fax",
  "agent_details.building_name": "Bldg Name",
  "agent_details.building_number": "Bldg No.",
  "agent_details.address_1_street": "Street",
  "agent_details.address_1": "Street",
  "agent_details.address_2": "Address 2",
  "agent_details.address_3": "Address 3",
  "agent_details.town_city": "Town / City",
  "agent_details.town_city_settlement": "Town / City",
  "agent_details.county": "County",
  "agent_details.postcode": "Postcode",
  "agent_details.country": "Country",
  "agent_details.is_the_applicant_an_individual_or_an_organisation_corporate_entity": "Individual / Org",
  "agent_contact.agent_name": "Name",
  "agent_contact.correspondence_email": "Email",
  "agent_contact.telephone_main": "Phone",
  // ─── Applicant Details ───────────────────────────────
  "applicant_details.title": "Title",
  "applicant_details.other_title": "Other Title",
  "applicant_details.first_name": "First Name",
  "applicant_details.last_name": "Surname",
  "applicant_details.surname": "Surname",
  "applicant_details.name": "Name",
  "applicant_details.company_organisation": "Company",
  "applicant_details.company": "Company",
  "applicant_details.organisation": "Organisation",
  "applicant_details.ref_number": "Ref No.",
  "applicant_details.email_address": "Email",
  "applicant_details.email": "Email",
  "applicant_details.telephone_number": "Phone",
  "applicant_details.telephone": "Phone",
  "applicant_details.extension_number": "Extension",
  "applicant_details.mobile_number": "Mobile",
  "applicant_details.mobile": "Mobile",
  "applicant_details.fax_number": "Fax",
  "applicant_details.fax": "Fax",
  "applicant_details.building_name": "Bldg Name",
  "applicant_details.building_number": "Bldg No.",
  "applicant_details.address_1_street": "Street",
  "applicant_details.address_1": "Street",
  "applicant_details.address_2": "Address 2",
  "applicant_details.address_3": "Address 3",
  "applicant_details.town_city": "Town / City",
  "applicant_details.town_city_settlement": "Town / City",
  "applicant_details.county": "County",
  "applicant_details.postcode": "Postcode",
  "applicant_details.country": "Country",
  "applicant_identity.organisation_name": "Company",
  "applicant_identity.individual_or_org": "Individual / Org",
  // ─── Site Address ────────────────────────────────────
  "site_address.planning_authority": "Authority",
  "site_address.address_1": "Address 1",
  "site_address.address_2": "Address 2",
  "site_address.address_3": "Address 3",
  "site_address.address_4": "Address 4",
  "site_address.address_5": "Address 5",
  "site_address.building_name": "Bldg Name",
  "site_address.building_number": "Bldg No.",
  "site_address.town_city": "Town / City",
  "site_address.town_city_settlement": "Town / City",
  "site_address.county": "County",
  "site_address.postcode": "Postcode",
  "site_address.post_code": "Postcode",
  "site_address.country": "Country",
  "site_address.grid_ref": "Grid Ref",
  "site_address.grid_reference": "Grid Ref",
  "site_address.easting": "Easting",
  "site_address.northing": "Northing",
  "site_address.uprn": "UPRN",
  "site_address.ward": "Ward",
  "site_address.parish": "Parish",
  "site_address.description": "Site Desc",
  "site_address.please_identify_describe_the_location_of_the_site_or_sites": "Location Desc",
  // ─── Pre-application ────────────────────────────────
  "pre_application_discussion.have_you_discussed_your_proposal_with_the_planning_authority": "Pre-app Discussion",
  // ─── Trees ──────────────────────────────────────────
  "trees_on_adjacent.are_there_any_trees_on_or_adjacent_to_the_application_site": "Trees on Site",
  // ─── Access & Parking ───────────────────────────────
  "access_and_parking.are_you_proposing_a_new_or_altered_vehicle_access_to_or_from_a_public_road": "Vehicle Access",
  // ─── Planning Service Interest ──────────────────────
  "planning_service_interest.is_the_applicant_or_the_applicant_s_spouse_partner_either_a_member_of_staff_within_the_planning_service_or_an_elected_member_of_the_planning_authority": "Planning Staff",
  // ─── Ownership / Certificates ───────────────────────
  "ownership_details.are_you_the_applicant_the_sole_owner_of_all_the_land": "Sole Owner",
  "ownership_details.is_any_of_the_land_part_of_an_agricultural_holding": "Agri Holding",
  "ownership_details.the_following_land_ownership_certificate_is_required_to_complete_this_section_of_the_proposal": "Cert Required",
  "ownership_details.signed": "Signed",
  "ownership_details.on_behalf_of": "On Behalf Of",
  "ownership_details.date": "Date",
  "ownership_details.please_tick_here_to_certify_this_certificate": "Certified",
  "ownership.certificate": "Certificate",
  "ownership.certificate_type": "Cert Type",
  "ownership.owner_name": "Owner Name",
  "ownership.owner_address": "Owner Address",
  "ownership.notice_served": "Notice Served",
  "ownership.notice_date": "Notice Date",
  // ─── Checklist items ────────────────────────────────
  "checklist_application_for_householder_application.a_have_you_provided_a_written_description_of_the_development_to_which_it_relates": "a) Written Desc",
  "checklist_application_for_householder_application.b_have_you_provided_the_postal_address_of_the_land_to_which_the_development_relates_or_if_the_land_in_question_has_no_postal_address_a_description_of_the_location_of_the_land": "b) Postal Address",
  "checklist_application_for_householder_application.c_have_you_provided_a_plan_sufficient_to_identify_the_land_to_which_it_relates_showing_the_situation_of_the_land_in_relation_to_the_locality_and_in_particular_in_relation_to_neighbouring_land": "c) Location Plan",
  "checklist_application_for_householder_application.d_have_you_provided_such_other_plans_and_drawings_as_are_necessary_to_describe_the_development_which_is_the_subject_of_the_application": "d) Plans & Drawings",
  "checklist_application_for_householder_application.e_have_you_provided_such_other_information_as_is_required_under_the_act_or_the_development_management_regulations": "e) Other Info",
  "checklist_application_for_householder_application.existing_and_proposed_elevations": "Elevations",
  "checklist_application_for_householder_application.existing_and_proposed_floor_plans": "Floor Plans",
  "checklist_application_for_householder_application.a_block_plan_site_layout": "Block Plan",
  "checklist_application_for_householder_application.a_location_plan": "Location Plan",
  "checklist_application_for_householder_application.photographs_and_or_photomontages": "Photos",
  // ─── Dates ──────────────────────────────────────────
  "dates.start_date": "Start Date",
  "dates.completion_date": "Completion",
  "dates.received_date": "Received",
  "dates.validated_date": "Validated",
  "dates.decision_date": "Decision",
  // ─── Fees / Payment ─────────────────────────────────
  "fees.fee_amount": "Fee",
  "fees.total_fee": "Total Fee",
  "fees.application_fee": "App Fee",
  "fees.payment_method": "Payment Method",
  "payment_payer.who_will_pay": "Who Pays",
};

// Regex patterns matched against the full raw label (before stripping)
const LABEL_REWRITES = [
  // Long planning questions → short labels
  [/are you an? applicant or an? agent/i, "Role"],
  [/is the applicant an individual or an organisation/i, "Individual / Org"],
  [/is the applicant an individual or a corporate/i, "Individual / Corp"],
  [/describe (?:accurately )?(?:the )?(?:work |development )?proposed/i, "Proposal"],
  [/description of (?:the )?(?:proposal|development|works)/i, "Proposal"],
  [/set out the variation sought/i, "Variation"],
  [/has (?:the )?(?:work|development).*(?:started|commenced|begun)/i, "Work Started"],
  [/(?:started|commenced) and\/or completed/i, "Work Started"],
  [/what (?:type|kind) of (?:planning )?application/i, "App Type"],
  [/type of (?:planning )?application/i, "App Type"],
  [/does the (?:site|proposal).*(?:include|involve).*tree/i, "Trees"],
  [/are there any trees/i, "Trees"],
  [/trees on or adjacent/i, "Trees"],
  [/is (?:the site|it) (?:within|in) a conservation area/i, "Conservation Area"],
  [/is (?:the application|this) for (?:a )?listed building/i, "Listed Building"],
  [/(?:is|does) the (?:site|proposal).*flood/i, "Flood Risk"],
  [/flood risk zone/i, "Flood Zone"],
  [/(?:new|existing|proposed).*(?:vehicle|vehicular) access/i, "Vehicle Access"],
  [/pedestrian and vehicle access/i, "Pedestrian & Vehicle"],
  [/is the site within.*(?:green belt|greenbelt)/i, "Green Belt"],
  [/pre.?application advice/i, "Pre-app Advice"],
  [/pre.?application reference/i, "Pre-app Ref"],
  [/(?:do you know.*)?authority (?:employee|member)/i, "Authority Employee"],
  [/applicant.*(?:member|employee).*(?:council|authority)/i, "Authority Employee"],
  [/can the site be seen.*public/i, "Visible from Public"],
  [/community infrastructure levy/i, "CIL"],
  [/(?:state|describe|give).*(?:existing|current) use/i, "Existing Use"],
  [/(?:state|describe|give).*proposed use/i, "Proposed Use"],
  [/existing use/i, "Existing Use"],
  [/proposed use/i, "Proposed Use"],
  [/how (?:will|is) foul sewage/i, "Foul Sewage"],
  [/foul sewage disposal/i, "Foul Sewage"],
  [/how (?:will|is) surface water/i, "Surface Water"],
  [/surface water drainage/i, "Surface Water"],
  [/(?:total )?site area/i, "Site Area"],
  [/gross (?:internal )?floor.?space/i, "Floor Space"],
  [/number of.*(?:dwelling|unit)/i, "Dwellings"],
  [/(?:residential|housing) unit/i, "Dwellings"],
  [/ownership certificate/i, "Ownership Cert"],
  [/full postal address/i, "Postal Address"],
  [/hazardous substance/i, "Hazardous Sub."],
  [/contaminated land/i, "Contamination"],
  [/protected species/i, "Protected Species"],
  [/waste.*(?:storage|recycling)/i, "Waste Storage"],
  [/refuse.*storage/i, "Waste Storage"],
  [/renewable energy/i, "Renewables"],
  [/sustainable drainage/i, "SuDS"],
  [/right of way/i, "Right of Way"],
  [/building height/i, "Bldg Height"],
  [/ridge height/i, "Ridge Height"],
  [/boundary treatment/i, "Boundary"],
  [/means of enclosure/i, "Enclosure"],
  [/external materials/i, "Ext. Materials"],
  [/external appearance/i, "Ext. Appearance"],
  [/design and access/i, "Design & Access"],
  [/planning statement/i, "Planning Stmt"],
  [/biodiversity.*(?:net gain|gain)/i, "Biodiversity NG"],
  [/energy performance/i, "EPC"],
  [/scheduled (?:ancient )?monument/i, "Monument"],
  [/world heritage/i, "World Heritage"],
  [/area of outstanding natural beauty|AONB/i, "AONB"],
  [/site of special scientific interest|SSSI/i, "SSSI"],
  [/special (?:protection|conservation) area/i, "SPA/SAC"],
  [/who will make the payment/i, "Who Pays"],
  [/how do you wish to pay/i, "Payment Method"],
  [/declaration.*i\/we hereby/i, "Declaration"],
  [/i\/we (?:hereby )?apply/i, "Declaration"],
  // Ownership / certificates
  [/sole owner of all the land/i, "Sole Owner"],
  [/agricultural holding/i, "Agri Holding"],
  [/land ownership certificate.*required/i, "Cert Required"],
  [/tick here to certify/i, "Certified"],
  // Pre-application
  [/discussed your proposal with the planning authority/i, "Pre-app Discussion"],
  [/have you discussed.*planning authority/i, "Pre-app Discussion"],
  // Checklist questions
  [/have you provided a written description/i, "a) Written Desc"],
  [/have you provided the postal address/i, "b) Postal Address"],
  [/have you provided a plan sufficient/i, "c) Location Plan"],
  [/have you provided such other plans/i, "d) Plans & Drawings"],
  [/have you provided such other information/i, "e) Other Info"],
  [/existing and proposed elevations/i, "Elevations"],
  [/existing and proposed floor plans/i, "Floor Plans"],
  [/block plan.*site layout/i, "Block Plan"],
  [/location plan/i, "Location Plan"],
  [/photographs? and.?or photomontage/i, "Photos"],
  // Vehicle access
  [/proposing a new or altered vehicle access/i, "Vehicle Access"],
  // Planning staff / elected member
  [/member of staff within the planning service/i, "Planning Staff"],
  [/elected member of the planning authority/i, "Planning Staff"],
  // Location description
  [/identify.*describe.*location.*site/i, "Location Desc"],
];

// Word-level shortening for remaining labels
const WORD_SHORTEN = [
  [/\bapplication\b/gi, "App"],
  [/\bdevelopment\b/gi, "Dev"],
  [/\bdescription\b/gi, "Desc"],
  [/\borganisation\b/gi, "Org"],
  [/\borganization\b/gi, "Org"],
  [/\bcorporate entity\b/gi, "Corp"],
  [/\btelecommunication[s]?\b/gi, "Telecom"],
  [/\benvironmental\b/gi, "Enviro"],
  [/\barchaeological\b/gi, "Archaeo"],
  [/\bcertificate\b/gi, "Cert"],
  [/\binformation\b/gi, "Info"],
  [/\badditional\b/gi, "Add'l"],
  [/\bcorrespondence\b/gi, "Corresp"],
  [/\btelephone\b/gi, "Phone"],
  [/\btelephone number\b/gi, "Phone"],
  [/\bnumber\b/gi, "No."],
  [/\bbuilding\b/gi, "Bldg"],
  [/\bsettlement\b/gi, ""],
  [/\bcontextual\b/gi, ""],
  // Strip filler phrasing
  [/\bplease\s+(?:describe|state|provide|give|enter|specify|select|choose)\s*/gi, ""],
  [/\bif (?:yes|no)[,.]?\s*/gi, ""],
  [/\bin (?:the )?(?:table )?below\s*/gi, ""],
  [/\bfor the purposes of\s*/gi, ""],
  [/\bin respect of\s*/gi, ""],
  [/\bin relation to\s*/gi, ""],
  [/\bas shown on\s*/gi, ""],
];

const abbreviateLabel = (raw, fieldKey, fieldKeyBase) => {
  if (!raw) return "Field";

  // 1. Try field_key_base lookup first (most precise — stable, clean key)
  const keysToTry = [fieldKeyBase, fieldKey].filter(Boolean).map(k => k.toLowerCase().trim());
  for (const fk of keysToTry) {
    if (FIELD_KEY_BASE_MAP[fk]) return FIELD_KEY_BASE_MAP[fk];
  }
  // Also try the second segment: "context.field_name" → "context.field_name"
  // And just the tail segment for generic matches
  const TAIL_MAP = {
    title: "Title", other_title: "Other Title",
    first_name: "First Name", firstname: "First Name",
    last_name: "Surname", surname: "Surname",
    name: "Name", full_name: "Full Name",
    company: "Company", company_organisation: "Company",
    organisation: "Organisation", organization: "Organisation",
    ref_number: "Ref No.",
    email: "Email", email_address: "Email",
    telephone: "Phone", telephone_number: "Phone",
    phone: "Phone", extension_number: "Extension",
    mobile: "Mobile", mobile_number: "Mobile",
    fax: "Fax", fax_number: "Fax",
    building_name: "Bldg Name", building_number: "Bldg No.",
    house_number: "House No.",
    address_1: "Street", address_1_street: "Street",
    address_2: "Address 2", address_3: "Address 3",
    address_4: "Address 4", address_5: "Address 5",
    town_city: "Town / City", town_city_settlement: "Town / City",
    town: "Town", city: "City", county: "County",
    postcode: "Postcode", post_code: "Postcode",
    country: "Country", uprn: "UPRN",
    planning_authority: "Authority",
    easting: "Easting", northing: "Northing",
    grid_ref: "Grid Ref", grid_reference: "Grid Ref",
    ward: "Ward", parish: "Parish",
    proposal: "Proposal", description: "Description",
    variation_sought: "Variation", variation_summary: "Variation",
    work_proposed: "Work Proposed",
    commencement: "Commenced", work_started: "Work Started",
    fee_amount: "Fee", total_fee: "Total Fee",
    payment_method: "Payment Method",
    certificate: "Certificate", certificate_type: "Cert Type",
    start_date: "Start Date", completion_date: "Completion",
    received_date: "Received", validated_date: "Validated",
    decision_date: "Decision", date: "Date",
    signed: "Signed", on_behalf_of: "On Behalf Of",
    applicant_or_agent: "Role",
    individual_or_org: "Individual / Org",
    organisation_name: "Company",
  };
  for (const fk of keysToTry) {
    // Try extracting first two segments (context.field)
    const parts = fk.split(".");
    if (parts.length >= 2) {
      const contextField = `${parts[0]}.${parts[1]}`;
      if (FIELD_KEY_BASE_MAP[contextField]) return FIELD_KEY_BASE_MAP[contextField];
    }
    // Try just the last segment
    const tail = parts[parts.length - 1];
    if (tail && TAIL_MAP[tail]) return TAIL_MAP[tail];
  }

  // 2. Try regex rewrites against full raw label
  for (const [pattern, replacement] of LABEL_REWRITES) {
    if (pattern.test(raw)) return replacement;
  }

  // 3. Strip context prefix(es) — "Applicant Details: Title:" → "Title"
  //    Walk backwards from the last colon-separated segment
  const colonParts = raw.split(/:\s*/);
  let label = colonParts.length > 1
    ? colonParts[colonParts.length - 1].trim() || colonParts[colonParts.length - 2].trim()
    : raw;

  // Strip asterisks, parenthetical suffixes, dash suffixes
  label = label
    .replace(/\s*\*+\s*/g, " ")
    .replace(/\s*\(.*$/, "")
    .replace(/\s*-\s+\w.*$/, "")
    .trim();

  if (!label) label = raw.replace(/[*:]+/g, " ").trim();

  // 4. Try regex rewrites again on stripped label
  for (const [pattern, replacement] of LABEL_REWRITES) {
    if (pattern.test(label)) return replacement;
  }

  // 5. Word-level shortening
  for (const [pattern, replacement] of WORD_SHORTEN) {
    label = label.replace(pattern, replacement);
  }

  // 6. Clean up
  label = label.replace(/\s+/g, " ").replace(/[?:,;]+$/, "").trim();

  // 7. Truncate at word boundary if still long
  if (label.length > 28) {
    const truncated = label.slice(0, 26).replace(/\s+\S*$/, "").trim();
    return truncated || label.slice(0, 26);
  }

  return label || "Field";
};

const ApplicationFormPanel = ({ application, formData, isLoading, error, onRetry }) => {
  const [query, setQuery] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  const [expandedSections, setExpandedSections] = useState(new Set(["applicant", "site", "proposal"]));
  const [copiedField, setCopiedField] = useState(null);
  const [quickFilter, setQuickFilter] = useState("all"); // all | missing | low | key
  const [controlsHidden, setControlsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollContainerRef = useRef(null);

  // Find the scrollable ancestor (modal body) and track scroll direction
  const controlsRef = useRef(null);
  useEffect(() => {
    const el = controlsRef.current;
    if (!el) return;
    // Walk up to find the scrollable container
    let scrollParent = el.parentElement;
    while (scrollParent && scrollParent !== document.documentElement) {
      const { overflow, overflowY } = getComputedStyle(scrollParent);
      if (/auto|scroll/.test(overflow + overflowY)) break;
      scrollParent = scrollParent.parentElement;
    }
    if (!scrollParent) return;
    scrollContainerRef.current = scrollParent;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = scrollParent.scrollTop;
        const delta = y - lastScrollY.current;
        // Only toggle after a meaningful scroll (8px threshold)
        if (delta > 8) setControlsHidden(true);
        else if (delta < -8) setControlsHidden(false);
        lastScrollY.current = y;
        ticking = false;
      });
    };
    scrollParent.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollParent.removeEventListener("scroll", onScroll);
  }, []);

  const normalized = useMemo(() => normalizePayload(formData), [formData]);
  const fields = useMemo(() => buildFields(normalized), [normalized]);
  const { formDoc, formDocLink, appLink } = useMemo(
    () => resolveApplicationFormDocumentLinks(application, formData),
    [application, formData]
  );

  const enrichedFields = useMemo(() => {
    return fields.map((field) => {
      const label = field.question_contextual || field.label || normalizeLabel(field.field_key || field.key || "field");
      const value = field.value || "";
      const group = field.group ? inferGroup(field.group) : inferGroup(`${field.context_id || ""} ${label}`);
      const searchKey = `${label} ${field.field_key || field.key || ""} ${field.context_label || ""} ${field.context_id || ""} ${field.group_id || ""}`;
      const isSensitive = isSensitiveKey(searchKey) || isSensitiveValue(value);
      const isKeyField = KEY_FIELD_PATTERNS.some((pattern) => pattern.test(searchKey.toLowerCase()));
      return {
        ...field,
        label,
        value,
        group: SECTION_DEFS.some((section) => section.id === group) ? group : "other",
        searchText: `${label} ${field.field_key || field.key || ""} ${field.context_label || ""} ${field.context_id || ""} ${value}`.toLowerCase(),
        isEmpty: !value,
        isSensitive,
        isKeyField,
      };
    });
  }, [fields]);

  const displayFields = useMemo(() => {
    return enrichedFields;
  }, [enrichedFields]);

  const filteredFields = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    const hideEmpty = viewMode !== "list" && quickFilter !== "missing";
    return displayFields.filter((field) => {
      if (hideEmpty && field.isEmpty) return false;
      if (quickFilter === "missing" && !field.isEmpty) return false;
      if (quickFilter === "low") {
        if (field.confidence === null || field.confidence === undefined) return false;
        if (Number(field.confidence) >= 60) return false;
      }
      if (quickFilter === "key" && !field.isKeyField) return false;
      if (!trimmedQuery) return true;
      return field.searchText.includes(trimmedQuery);
    });
  }, [displayFields, query, viewMode, quickFilter]);

  const sections = useMemo(() => {
    const grouped = new Map();
    filteredFields.forEach((field) => {
      const group = field.group || "other";
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group).push(field);
    });

    return SECTION_DEFS
      .filter((section) => grouped.has(section.id))
      .map((section) => ({
        ...section,
        items: grouped.get(section.id) || [],
      }))
      .sort((a, b) => a.priority - b.priority);
  }, [filteredFields]);

  const sectionTotals = useMemo(() => {
    const totals = new Map();
    enrichedFields.forEach((field) => {
      const group = field.group || "other";
      if (!totals.has(group)) {
        totals.set(group, { total: 0, filled: 0 });
      }
      const entry = totals.get(group);
      entry.total += 1;
      if (!field.isEmpty) entry.filled += 1;
    });
    return totals;
  }, [enrichedFields]);

  const insights = useMemo(() => extractInsights(enrichedFields), [enrichedFields]);

  const stats = useMemo(() => {
    const summary = buildFormDataSummary(enrichedFields);
    return {
      ...summary,
      confidenceAvailable: summary.avgConfidence !== null,
    };
  }, [enrichedFields]);

  const contactCards = useMemo(
    () => ({
      agent: buildContactCard(enrichedFields, "agent_details", "Agent"),
      applicant: buildContactCard(enrichedFields, "applicant_details", "Applicant"),
    }),
    [enrichedFields]
  );

  const applicationSignals = useMemo(
    () => buildApplicationSignals(enrichedFields, application, contactCards),
    [enrichedFields, application, contactCards]
  );

  const safeParsed = useMemo(() => {
    return normalized.parsed;
  }, [normalized.parsed]);

  const visibleFieldCount = useMemo(() => {
    return displayFields.filter((field) => !(field.isEmpty && viewMode !== "list")).length;
  }, [displayFields, viewMode]);

  const showResultsSummary = Boolean(query.trim()) || quickFilter !== "all";

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const copyFieldValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(value);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const exportData = () => {
    const exportObj = {
      application: {
        reference: application?.reference_no || application?.ref,
        address: application?.address || application?.location,
      },
      extraction: {
        version: normalized.meta?.version || 2,
        parsedAt: normalized.meta?.parsedAt,
        stats,
      },
      fields: enrichedFields.map(f => ({
        section: f.group,
        label: f.label,
        value: f.value,
        confidence: f.confidence,
        fieldKey: f.field_key || null,
        contextId: f.context_id || null,
        contextLabel: f.context_label || null,
      })),
    };

    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `application-form-${application?.reference_no || 'data'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const scrollToSection = (sectionId, resetFilters = true) => {
    if (!sectionId) return;
    if (resetFilters) {
      setQuery("");
      setQuickFilter("all");
    }
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
    setTimeout(() => {
      const el = document.getElementById(`form-section-${sectionId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
  };

  const handleOpenFormDoc = async () => {
    if (!formDocLink) return;
    try {
      await openPlanningDoc(formDocLink, appLink || "", formDoc, undefined, {
        reference: application?.reference_no || application?.ref || "",
        council: application?.source_council || application?.council_slug || application?.council || "",
        title: formDoc?.source_desc || formDoc?.title || formDoc?.name || "Application form",
        previewUrl: formDoc?.thumbnail_url || formDoc?.thumbnail,
      });
    } catch (error) {
      console.error("Failed to open form document", error);
    }
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-5 sm:p-6 bg-[var(--c-bg)]"
      >
        <div
          className="rounded-xl bg-[var(--c-surface-hover)] p-10 text-center"
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 36px rgba(0,0,0,0.24)" }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
            className="mx-auto mb-5 h-12 w-12 rounded-full border-2 border-[var(--c-accent)]/70 border-t-transparent"
          />
          <p className="text-[var(--c-text-primary)] font-medium text-base leading-5">
            Extracting Form Data
          </p>
          <p className="text-[var(--c-text-secondary)] text-sm leading-5 mt-1">
            Parsing structured fields from the submitted application form.
          </p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-5 sm:p-6 bg-[var(--c-bg)]"
      >
        <div
          className="rounded-xl bg-[var(--c-surface-hover)] p-10 text-center"
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 36px rgba(0,0,0,0.24)" }}
        >
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-rose-300/90" />
          <p className="text-[var(--c-text-primary)] font-medium text-base leading-5">Form Extraction Failed</p>
          <p className="text-[var(--c-text-secondary)] text-sm leading-5 mt-1 max-w-xl mx-auto">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-5 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wide text-rose-600 bg-rose-500/15 hover:bg-rose-500/22 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
              style={{ border: "1px solid rgba(251, 113, 133, 0.28)" }}
            >
              Retry Extraction
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  if (application?.planning_delivery === 'shared' && isLondonCouncil(application.source_council) &&
      formDocLink && !enrichedFields.length && !normalized.meta?.parsedAt && normalized.meta?.version == null) {
    return (
      <div role="status" className="p-5 sm:p-6 bg-[var(--c-bg)]">
        <div className="border border-[var(--c-border)] bg-[var(--c-surface)] p-6">
          <h2 className="text-lg text-[var(--c-text-primary)]">Form processing pending</h2>
          <p className="mt-2 text-sm text-[var(--c-text-secondary)]">
            The submitted form PDF is available. Structured fields have not been processed yet.
          </p>
          <a href={formDocLink} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm underline">
            Open submitted form PDF
          </a>
        </div>
      </div>
    );
  }

  if (!enrichedFields.length && !normalized.parsed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-5 sm:p-6 bg-[var(--c-bg)]"
      >
        <div
          className="rounded-xl bg-[var(--c-surface-hover)] p-10 text-center"
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 36px rgba(0,0,0,0.24)" }}
        >
          <FileSearch className="mx-auto mb-4 h-10 w-10 text-[var(--c-accent)]" />
          <p className="text-[var(--c-text-primary)] font-medium text-base leading-5">No Form Data Available</p>
          <p className="text-[var(--c-text-secondary)] text-sm leading-5 mt-1 max-w-xl mx-auto">
            This application does not have extracted form fields yet. Extraction appears once a valid form payload is available.
          </p>
        </div>
      </motion.div>
    );
  }

  const parsedAt = formatDate(normalized.meta?.parsedAt);
  const quickFilterOptions = [
    { id: "all", label: "All", color: RETRO_COLORS.green },
    { id: "missing", label: "Missing", color: RETRO_COLORS.red },
    { id: "low", label: "Low confidence", color: RETRO_COLORS.orange },
    { id: "key", label: "Key fields", color: RETRO_COLORS.blue },
  ];
  const TEXT_TIER = {
    primary: "text-[var(--c-text-primary)] font-medium",
    secondary: "text-[var(--c-text-secondary)]",
    muted: "text-[var(--c-text-secondary)]/80",
  };
  const compactLabel = "text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono font-semibold uppercase tracking-[0.16em] text-[var(--c-text-secondary)]";
  const accentSectionLabel = "text-[17px] leading-6 sm:text-[1.2rem] sm:leading-6 font-mono font-semibold uppercase tracking-[0.14em] text-[var(--c-accent)]";
  const peopleRoleLabelMobile = "text-[12.5px] leading-5 font-mono font-semibold uppercase tracking-[0.14em] text-[var(--c-accent)]";
  const peopleRoleLabelDesktop = "w-[94px] text-[12.5px] leading-5 font-mono font-semibold uppercase tracking-[0.14em] text-[var(--c-accent)]";
  const peopleCountBadge = "inline-flex min-w-[46px] items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-0.5 text-[11px] leading-5 font-mono text-[var(--c-text-secondary)]";
  const peoplePrimaryText = `text-[14px] leading-6 ${TEXT_TIER.primary}`;
  const peopleSecondaryText = `text-[13px] leading-5 ${TEXT_TIER.secondary}`;
  const peopleMutedText = `text-[13px] leading-5 ${TEXT_TIER.muted}`;
  const agentName = contactCards.agent.name || "Not found";
  const agentCompany =
    contactCards.agent.company &&
    contactCards.agent.company !== "Not found" &&
    contactCards.agent.company !== contactCards.agent.name &&
    !(contactCards.agent.name && contactCards.agent.name.toLowerCase().includes(contactCards.agent.company.toLowerCase()))
      ? contactCards.agent.company
      : null;
  const agentEmailDisplay =
    contactCards.agent.email && contactCards.agent.email !== "Not found"
      ? contactCards.agent.email
      : "Email not found";
  const agentPhoneDisplay =
    contactCards.agent.phone && contactCards.agent.phone !== "Not found"
      ? contactCards.agent.phone
      : "Phone not found";
  const applicantName = contactCards.applicant.name || "Not found";
  const applicantCompanyRaw = contactCards.applicant.company && contactCards.applicant.company !== "Not found"
    ? contactCards.applicant.company
    : null;
  const applicantCompany =
    applicantCompanyRaw &&
    !(contactCards.applicant.name && contactCards.applicant.name.toLowerCase().includes(applicantCompanyRaw.toLowerCase()))
      ? applicantCompanyRaw
      : null;
  const heroSignals = applicationSignals.slice(0, 6);
  const metricInsights = insights.slice(0, 12);
  const statCards = [
    {
      icon: BarChart3,
      label: "Coverage",
      value: `${stats.coverage}%`,
      secondary: `${stats.filledFields}/${stats.totalFields} fields`,
      progress: stats.coverage,
    },
    {
      icon: Grid,
      label: "Context",
      value: `${stats.contextualCoverage}%`,
      secondary: `${stats.contextualFields}/${stats.totalFields} contextual`,
      progress: stats.contextualCoverage,
    },
    {
      icon: TrendingUp,
      label: "Avg confidence",
      value: stats.confidenceAvailable ? `${stats.avgConfidence}%` : "n/a",
      secondary: stats.confidenceAvailable ? "Field confidence score" : "Confidence unavailable",
    },
    {
      icon: ShieldCheck,
      label: "High confidence",
      value: stats.highConfidenceRate !== null ? `${stats.highConfidenceRate}%` : "n/a",
      secondary: stats.highConfidenceRate !== null ? "Fields at 80%+" : "Rate unavailable",
    },
  ];

  return (
    <div className="relative w-full bg-[var(--c-bg)]">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8 py-5 space-y-4">
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="relative overflow-hidden rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface-hover)]"
          style={{
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 34px rgba(0,0,0,0.22)",
            backgroundImage: "radial-gradient(120% 140% at 100% 0%, rgba(242,205,77,0.08), transparent 55%)",
          }}
        >
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, var(--c-accent) 24%, var(--c-accent) 76%, transparent 100%)",
              opacity: 0.22,
            }}
          />

          <div className="relative px-4 pt-5 pb-4 sm:px-5 sm:pt-6 sm:pb-5 space-y-4">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-[var(--c-accent)]" />
                  <span className="text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono font-semibold uppercase tracking-[0.16em] text-[var(--c-text-secondary)]">
                    Planning Insights
                  </span>
                </div>
                <p className={`mt-1 text-sm leading-6 ${TEXT_TIER.secondary}`}>
                  Key details pulled from the submitted application form.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleOpenFormDoc}
                  disabled={!formDocLink}
                  title={formDocLink ? "Open submitted form PDF" : "No form PDF available"}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono text-[var(--c-accent)] bg-[var(--c-surface)] hover:bg-[var(--c-surface)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                  style={{ border: "1px solid var(--c-border)" }}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Form PDF
                </button>
                {parsedAt && (
                  <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono text-[var(--c-text-secondary)] bg-[var(--c-surface)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                    Parsed {parsedAt}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 2xl:grid-cols-[1.1fr_0.9fr] gap-3">
              <div
                className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-hover)] px-4 py-3"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 20px rgba(0,0,0,0.16)" }}
              >
                <div className={accentSectionLabel}>Key Signals</div>
                {heroSignals.length > 0 ? (
                  <div className="mt-1.5">
                    {heroSignals.map((signal, idx) => (
                      <button
                        key={`${signal.id || idx}-hero`}
                        type="button"
                        onClick={() => signal.sectionId && scrollToSection(signal.sectionId)}
                        disabled={!signal.sectionId}
                        className={`w-full text-left py-1.5 ${idx > 0 ? "border-t border-[var(--c-border)]" : ""} ${
                          signal.sectionId ? "hover:bg-[var(--c-surface)] transition-colors rounded-sm" : ""
                        }`}
                      >
                        <p className="text-[11px] leading-5 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)]">
                          {signal.label}
                        </p>
                        <p className={`text-sm leading-6 ${signal.value === "Not found" ? TEXT_TIER.muted : TEXT_TIER.primary}`}>
                          {signal.value === "Not found" ? "Not found" : signal.value}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className={`mt-1 text-sm leading-6 ${TEXT_TIER.muted}`}>No key signals extracted.</p>
                )}
{/* Metric chips shown only in Detected Metrics section below */}
              </div>

              <div
                className="relative overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-hover)]"
                style={{
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 24px rgba(0,0,0,0.18)",
                  backgroundImage: "linear-gradient(165deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01))",
                }}
              >
                <div className="px-4 sm:px-5 pt-4 pb-2">
                  <div className={accentSectionLabel}>People</div>
                </div>
                <div className="sm:hidden">
                  <div className="px-4 py-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5">
                          <Briefcase className="w-4 h-4 text-[var(--c-text-secondary)]/80" />
                          <span className={peopleRoleLabelMobile}>Agent</span>
                        </div>
                        <div className="mt-2 pl-6 space-y-0.5">
                          <p className={`whitespace-normal break-words ${peoplePrimaryText}`}>{agentName}</p>
                          {agentCompany && (
                            <p className={`whitespace-normal break-words ${peopleSecondaryText}`}>{agentCompany}</p>
                          )}
                        </div>
                      </div>
                      <span className={`${peopleCountBadge} mt-0.5`}>
                        {contactCards.agent.availableCount}/4
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-[var(--c-text-secondary)]/80" />
                      <span className={peopleRoleLabelMobile}>Contact</span>
                    </div>
                    <div className="mt-2 pl-6 space-y-1.5">
                      <p className={`inline-flex min-w-0 items-center gap-1.5 ${contactCards.agent.email && contactCards.agent.email !== "Not found" ? peopleSecondaryText : peopleMutedText}`}>
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="min-w-0 break-all">{agentEmailDisplay}</span>
                      </p>
                      <p className={`inline-flex min-w-0 items-center gap-1.5 ${contactCards.agent.phone && contactCards.agent.phone !== "Not found" ? peopleSecondaryText : peopleMutedText}`}>
                        <Phone className="w-3 h-3 flex-shrink-0" />
                        <span className="min-w-0 break-all">{agentPhoneDisplay}</span>
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-[var(--c-border)] px-4 py-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5">
                          <User className="w-4 h-4 text-[var(--c-text-secondary)]/80" />
                          <span className={peopleRoleLabelMobile}>Applicant</span>
                        </div>
                        <div className="mt-2 pl-6 space-y-0.5">
                          <p className={`whitespace-normal break-words ${peoplePrimaryText}`}>{applicantName}</p>
                          {applicantCompany && <p className={`whitespace-normal break-words ${peopleSecondaryText}`}>{applicantCompany}</p>}
                          <p className={`text-[12px] leading-5 italic ${TEXT_TIER.muted}`}>Contact via agent</p>
                        </div>
                      </div>
                      <span className={`${peopleCountBadge} mt-0.5`}>
                        {contactCards.applicant.availableCount}/4
                      </span>
                    </div>
                  </div>
                </div>

                <div className="hidden sm:block">
                  <div className="px-5 py-4">
                    <div className="grid grid-cols-[18px_94px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5">
                      <Briefcase className="mt-1 w-4 h-4 text-[var(--c-text-secondary)]/80" />
                      <span className={peopleRoleLabelDesktop}>Agent</span>
                      <div className="min-w-0 flex flex-col gap-0.5">
                        <span className={`min-w-0 whitespace-normal break-words ${peoplePrimaryText}`}>{agentName}</span>
                        {agentCompany && (
                          <span className={`min-w-0 whitespace-normal break-words ${peopleSecondaryText}`}>{agentCompany}</span>
                        )}
                      </div>
                      <span className={peopleCountBadge}>{contactCards.agent.availableCount}/4</span>
                    </div>
                  </div>

                  <div className="border-t border-[var(--c-border)] bg-[var(--c-surface)] px-5 py-3.5">
                    <div className="grid grid-cols-[18px_94px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5">
                      <Phone className="mt-1 w-4 h-4 text-[var(--c-text-secondary)]/80" />
                      <span className={peopleRoleLabelDesktop}>Contact</span>
                      <div className="min-w-0 grid grid-cols-1 gap-y-1.5">
                        <span className={`inline-flex min-w-0 items-center gap-1.5 ${contactCards.agent.email && contactCards.agent.email !== "Not found" ? peopleSecondaryText : peopleMutedText}`}>
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="min-w-0 break-all">{agentEmailDisplay}</span>
                        </span>
                        <span className={`inline-flex min-w-0 items-center gap-1.5 ${contactCards.agent.phone && contactCards.agent.phone !== "Not found" ? peopleSecondaryText : peopleMutedText}`}>
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span className="min-w-0 break-all">{agentPhoneDisplay}</span>
                        </span>
                      </div>
                      <span className="w-[46px]" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="border-t border-[var(--c-border)] px-5 py-4">
                    <div className="grid grid-cols-[18px_94px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5">
                      <User className="mt-1 w-4 h-4 text-[var(--c-text-secondary)]/80" />
                      <span className={peopleRoleLabelDesktop}>Applicant</span>
                      <div className="min-w-0 flex flex-col gap-0.5">
                        <span className={`min-w-0 whitespace-normal break-words ${peoplePrimaryText}`}>{applicantName}</span>
                        {applicantCompany && <span className={`min-w-0 whitespace-normal break-words ${peopleSecondaryText}`}>{applicantCompany}</span>}
                        <span className={`text-[12px] leading-5 italic ${TEXT_TIER.muted}`}>Contact via agent</span>
                      </div>
                      <span className={peopleCountBadge}>{contactCards.applicant.availableCount}/4</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coverage / confidence stat cards hidden — low signal-to-noise ratio */}
            {/* <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
              {statCards.map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface-hover)] px-3 py-2.5"
                  style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 18px rgba(0,0,0,0.14)" }}
                >
                  <div className="flex items-center justify-between">
                    <span className={compactLabel}>{stat.label}</span>
                    <stat.icon className="w-3.5 h-3.5 text-[var(--c-accent)]" />
                  </div>
                  <p className={`mt-1 text-lg leading-6 ${TEXT_TIER.primary}`}>{stat.value}</p>
                  <p className={`text-[11px] leading-5 ${TEXT_TIER.muted}`}>{stat.secondary}</p>
                  {typeof stat.progress === "number" && (
                    <div className="mt-1.5 h-1 rounded-full bg-white/[0.08] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${stat.progress}%` }}
                        transition={{ duration: 0.75, ease: "easeOut" }}
                        className="h-full bg-[var(--c-accent)]/70"
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </div> */}
          </div>
        </motion.section>

      {/* Application Signals */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-xl bg-[var(--c-surface-hover)] p-4 sm:p-5"
        style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 24px rgba(0,0,0,0.2)" }}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className={accentSectionLabel}>Application Signals</p>
          <span className={`text-[11px] leading-5 sm:text-xs sm:leading-5 ${TEXT_TIER.muted}`}>
            Click to jump to related sections
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {applicationSignals.map((signal, idx) => {
            const Icon =
              signal.id === "submitted-by"
                ? Users
                : signal.id === "work-started"
                  ? Calendar
                  : signal.id === "site-location"
                    ? MapPin
                    : FileText;
            const isClickable = Boolean(signal.sectionId);
            return (
              <motion.button
                key={signal.id || idx}
                type="button"
                disabled={!isClickable}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 + idx * 0.03 }}
                onClick={() => isClickable && scrollToSection(signal.sectionId)}
                className={`rounded-md bg-[var(--c-surface-hover)] px-3 py-2 text-left transition-colors ${
                  signal.long ? "lg:col-span-2" : ""
                } ${isClickable ? "hover:bg-[var(--c-surface)]" : ""}`}
                style={{
                  border: "1px solid var(--c-border)",
                  opacity: isClickable ? 1 : 0.92,
                  cursor: isClickable ? "pointer" : "default",
                }}
                title={isClickable ? "Jump to related section" : undefined}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-[var(--c-accent)]" />
                  <p className="text-[11px] leading-5 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)]">
                    {signal.label}
                  </p>
                </div>
                <p className={`mt-1 text-sm leading-6 ${signal.value === "Not found" ? TEXT_TIER.muted : TEXT_TIER.primary}`}>
                  {signal.value}
                </p>
              </motion.button>
            );
          })}
        </div>

        {insights.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--c-border)]">
            <p className={accentSectionLabel}>Detected Metrics</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {insights.map((insight, idx) => {
                const isClickable = Boolean(insight.sectionId);
                return (
                  <button
                    key={`${insight.label}-${idx}`}
                    type="button"
                    disabled={!isClickable}
                    onClick={() => isClickable && scrollToSection(insight.sectionId)}
                    className="px-2 py-0.5 rounded-full text-[11px] leading-5 sm:text-xs sm:leading-5 font-mono text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] transition-colors disabled:opacity-80"
                    style={{ border: "1px solid var(--c-border)" }}
                  >
                    {insight.label}: {insight.value}
                    {insight.unit ? ` ${insight.unit}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      {/* Controls */}
      <div
        ref={controlsRef}
        className={`sticky top-2 z-20 rounded-xl bg-[var(--c-surface-hover)] p-3 sm:p-4 transition-all duration-300 ${controlsHidden ? "opacity-0 -translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"}`}
        style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 22px rgba(0,0,0,0.18)" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col xl:flex-row xl:items-center gap-3"
        >
          {/* Search */}
          <div className="relative xl:w-[320px] w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--c-text-secondary)]/80" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fields, labels, context"
              className="w-full h-9 pl-9 pr-3 rounded-md bg-[var(--c-surface-hover)] text-sm text-[var(--c-text-primary)] placeholder:text-[var(--c-text-secondary)]/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
              style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)", border: "1px solid var(--c-border)" }}
            />
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5">
            {quickFilterOptions.map((option) => {
              const active = quickFilter === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setQuickFilter(option.id)}
                  className={`px-2.5 py-1.5 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] transition-colors ${
                    active ? "text-[var(--c-text-primary)]" : "text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                  }`}
                  style={{
                    background: active ? `${option.color}33` : "rgba(255,255,255,0.03)",
                    border: `1px solid ${active ? `${option.color}66` : "var(--c-border)"}`,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {/* View mode and actions */}
          <div className="flex items-center gap-1.5 xl:ml-auto">
            <div
              className="inline-flex items-center rounded-md p-0.5 bg-[var(--c-surface-hover)]"
              style={{ border: "1px solid var(--c-border)" }}
            >
              <button
                onClick={() => setViewMode("cards")}
                className={`px-2 py-1 rounded text-xs transition-colors ${
                  viewMode === "cards" ? "bg-[var(--c-accent)] text-[var(--c-bg)]" : "text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                }`}
                title="Card view"
              >
                <Grid className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-2 py-1 rounded text-xs transition-colors ${
                  viewMode === "list" ? "bg-[var(--c-accent)] text-[var(--c-bg)]" : "text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                }`}
                title="List view"
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              onClick={exportData}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] hover:text-[var(--c-text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
              style={{ border: "1px solid var(--c-border)" }}
              title="Export data as JSON"
            >
              <Download className="w-3.5 h-3.5" />
              Export
            </button>

            <button
              onClick={() => setShowRaw((prev) => !prev)}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)] ${
                showRaw
                  ? "text-[var(--c-accent)] bg-[var(--c-accent)]/15"
                  : "text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] hover:text-[var(--c-text-primary)]"
              }`}
              style={{ border: `1px solid ${showRaw ? "rgba(242, 205, 77, 0.25)" : "var(--c-border)"}` }}
              title="Toggle raw data view"
            >
              {showRaw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              Raw
            </button>
          </div>
        </motion.div>
        {showResultsSummary && (
          <p className={`mt-2 text-[11px] leading-5 sm:text-xs sm:leading-5 font-mono ${TEXT_TIER.muted}`}>
            Showing {filteredFields.length} results{visibleFieldCount ? ` of ${visibleFieldCount}` : ""}.
          </p>
        )}
      </div>

      {/* Raw data view */}
      <AnimatePresence>
        {showRaw && (
          <motion.div
            initial={{ opacity: 0, y: -6, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden rounded-xl bg-[var(--c-surface-hover)]"
            style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 12px 26px rgba(0,0,0,0.2)" }}
          >
            <div className="p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className={compactLabel}>Raw Extracted Data</p>
                <button
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(safeParsed ?? {}, null, 2))}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] leading-4 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] hover:text-[var(--c-text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                  style={{ border: "1px solid var(--c-border)" }}
                >
                  <Copy className="w-3 h-3" />
                  Copy JSON
                </button>
              </div>
              <pre
                className={`max-h-80 overflow-auto rounded-md p-3 text-[11px] leading-5 font-mono ${TEXT_TIER.secondary}`}
                style={{ background: "rgba(0,0,0,0.22)", border: "1px solid var(--c-border)" }}
              >
                {JSON.stringify(safeParsed ?? {}, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sections */}
      <div className="space-y-4 pb-4">
          <AnimatePresence mode="wait">
            {viewMode === "cards" ? (
              <motion.div
                key="cards"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="space-y-3"
              >
                {sections.map((section, sectionIdx) => {
                  const Icon = section.icon;
                  const isExpanded = expandedSections.has(section.id);
                  const totals = sectionTotals.get(section.id);

                  return (
                    <motion.div
                      key={section.id}
                      id={`form-section-${section.id}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: sectionIdx * 0.03 }}
                      className="rounded-xl bg-[var(--c-surface-hover)] overflow-hidden"
                      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 24px rgba(0,0,0,0.2)" }}
                    >
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[var(--c-surface)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="h-8 w-8 rounded-md flex items-center justify-center"
                            style={{
                              color: section.color,
                              background: `${section.color}1F`,
                              border: `1px solid ${section.color}4A`,
                            }}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <p className={compactLabel}>{section.label}</p>
                            <p className={`text-[11px] leading-5 ${TEXT_TIER.muted}`}>
                              {totals
                                ? `${totals.filled}/${totals.total} populated`
                                : `${section.items.length} field${section.items.length !== 1 ? "s" : ""}`}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.22 }}
                          className="text-[var(--c-text-secondary)]/80"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </motion.div>
                      </button>

                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
                              {section.items.map((field, fieldIdx) => {
                                const confidenceColor = getConfidenceColor(field.confidence);
                                const isLong = field.value && field.value.length > 60;
                                const shortLabel = abbreviateLabel(field.label, field.field_key || field.key, field.field_key_base);
                                return (
                                  <div
                                    key={field.key}
                                    className={`group/field flex items-baseline gap-2 py-2 border-t border-[var(--c-border)] min-w-0 ${isLong ? "md:col-span-2" : ""}`}
                                    title={field.label !== shortLabel ? field.label : undefined}
                                  >
                                    <span className="flex-shrink-0 max-w-[45%] text-[11px] leading-5 font-mono uppercase tracking-[0.1em] text-[var(--c-text-secondary)]/80 truncate">
                                      {highlightMatches(shortLabel, query)}
                                    </span>
                                    <span className={`min-w-0 text-[13px] leading-5 break-words ${field.value ? TEXT_TIER.primary : TEXT_TIER.muted}`}>
                                      {field.value ? highlightMatches(field.value, query) : "—"}
                                    </span>
                                    {field.confidence !== null && field.confidence !== undefined && (
                                      <span
                                        className="flex-shrink-0 inline-flex items-center px-1 py-0 rounded text-[10px] leading-4 font-mono"
                                        style={{
                                          color: confidenceColor,
                                          background: `${confidenceColor}18`,
                                          border: `1px solid ${confidenceColor}30`,
                                        }}
                                      >
                                        {field.confidence}%
                                      </span>
                                    )}
                                    <button
                                      onClick={() => field.value && copyFieldValue(field.value)}
                                      disabled={!field.value}
                                      className="flex-shrink-0 opacity-0 group-hover/field:opacity-100 transition-opacity p-0.5 rounded hover:bg-[var(--c-surface)] text-[var(--c-text-secondary)]/80 disabled:opacity-40 disabled:cursor-not-allowed"
                                      title="Copy value"
                                    >
                                      {copiedField === field.value ? (
                                        <Check className="w-3 h-3 text-emerald-300" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="rounded-xl overflow-hidden bg-[var(--c-surface-hover)]"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 24px rgba(0,0,0,0.2)" }}
              >
                <div>
                  {filteredFields.map((field, idx) => {
                    const section = SECTION_DEFS.find(s => s.id === field.group);
                    const Icon = section?.icon || FileText;
                    const sectionColor = section?.color || RETRO_COLORS.blue;
                    const confidenceColor = getConfidenceColor(field.confidence);

                    return (
                      <div
                        key={field.key}
                        className={`group/list px-4 py-2.5 ${idx > 0 ? "border-t border-[var(--c-border)]" : ""} hover:bg-[var(--c-surface)] transition-colors`}
                      >
                        <div className="grid grid-cols-1 xl:grid-cols-[34px_220px_minmax(0,1fr)_auto] gap-2 xl:gap-3 items-start">
                          <div
                            className="h-6 w-6 rounded inline-flex items-center justify-center"
                            style={{
                              color: sectionColor,
                              background: `${sectionColor}1A`,
                              border: `1px solid ${sectionColor}40`,
                            }}
                          >
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] leading-5 sm:text-xs sm:leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)]">
                                {section?.label || "Other"}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] leading-5 font-mono uppercase tracking-[0.14em] text-[var(--c-text-secondary)]/80">
                                {highlightMatches(field.label, query)}
                            </p>
                          </div>
                          <div className="min-w-0">
                            {(field.context_label || field.field_key) && (
                              <p className={`text-[11px] leading-5 ${TEXT_TIER.muted}`}>
                                {field.context_label || field.field_key}
                              </p>
                            )}
                            <p className={`mt-0.5 text-sm leading-5 break-words whitespace-pre-wrap ${field.value ? TEXT_TIER.primary : TEXT_TIER.muted}`}>
                              {field.value ? highlightMatches(field.value, query) : "Not provided"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {field.confidence !== null && field.confidence !== undefined && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] leading-5 font-mono whitespace-nowrap"
                                style={{
                                  color: confidenceColor,
                                  background: `${confidenceColor}22`,
                                  border: `1px solid ${confidenceColor}3A`,
                                }}
                              >
                                {field.confidence}%
                              </span>
                            )}
                            <button
                              onClick={() => field.value && copyFieldValue(field.value)}
                              disabled={!field.value}
                              className="opacity-0 group-hover/list:opacity-100 transition-opacity p-1 rounded hover:bg-[var(--c-surface)] text-[var(--c-text-secondary)]/80 disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Copy value"
                            >
                              {copiedField === field.value ? (
                                <Check className="w-3.5 h-3.5 text-emerald-300" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Empty state for filtered results */}
        {showResultsSummary && filteredFields.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl bg-[var(--c-surface-hover)] p-10 text-center"
            style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 24px rgba(0,0,0,0.2)" }}
          >
            <Search className="mx-auto mb-3 h-8 w-8 text-[var(--c-text-secondary)]/80" />
            <p className={`text-sm leading-5 ${TEXT_TIER.primary}`}>No results for current filters</p>
            <p className={`text-xs leading-5 mt-1 ${TEXT_TIER.secondary}`}>
              No fields match the current filters{query ? ` for "${query}"` : ""}. Try different keywords or reset filters.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ApplicationFormPanel;
